export const DEFAULT_SNAP_OPTIONS = {
  snapDistance: 14,
  centerSnapDistance: 9,
  snapGap: 3,
  overlapTolerance: 22,
};

export function normalizeTableList(tables) {
  if (Array.isArray(tables)) return tables;
  if (tables && typeof tables === "object") return Object.values(tables);
  return [];
}

export function clampPosition(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function getTableRect(table) {
  const left = Number(table?.left) || 0;
  const top = Number(table?.top) || 0;
  const width = Number(table?.width) || 50;
  const height = Number(table?.height) || 50;

  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

export function getRectFromPosition(left, top, width, height) {
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

export function rangesOverlapWithTolerance(
  aStart,
  aEnd,
  bStart,
  bEnd,
  tolerance,
) {
  return aEnd + tolerance >= bStart && bEnd + tolerance >= aStart;
}

export function getBestSnapPosition({
  tableId,
  proposedLeft,
  proposedTop,
  width = 50,
  height = 50,
  otherTables = [],
  options = {},
}) {
  const { snapDistance, centerSnapDistance, snapGap, overlapTolerance } = {
    ...DEFAULT_SNAP_OPTIONS,
    ...options,
  };

  const normalizedTables = normalizeTableList(otherTables);

  let snappedLeft = proposedLeft;
  let snappedTop = proposedTop;

  let bestX = {
    distance: Infinity,
    value: proposedLeft,
    guide: null,
  };

  let bestY = {
    distance: Infinity,
    value: proposedTop,
    guide: null,
  };

  const movingRect = getRectFromPosition(
    proposedLeft,
    proposedTop,
    Number(width) || 50,
    Number(height) || 50,
  );

  normalizedTables.forEach((otherTable) => {
    const isOtherTablePlaced =
      otherTable.isPlaced === true ||
      otherTable.isPlaced === 1 ||
      otherTable.isPlaced === "1" ||
      otherTable.isPlaced === "true";

    if (!isOtherTablePlaced) return;
    if (String(otherTable.id) === String(tableId)) return;

    const otherRect = getTableRect(otherTable);

    const verticalOverlap = rangesOverlapWithTolerance(
      movingRect.top,
      movingRect.bottom,
      otherRect.top,
      otherRect.bottom,
      overlapTolerance,
    );

    const horizontalOverlap = rangesOverlapWithTolerance(
      movingRect.left,
      movingRect.right,
      otherRect.left,
      otherRect.right,
      overlapTolerance,
    );

    const guide = {
      tableId: otherTable.id,
      left: otherRect.left,
      top: otherRect.top,
      width: otherRect.width,
      height: otherRect.height,
    };

    if (verticalOverlap) {
      const attachToRightSide = otherRect.right + snapGap;
      const attachToRightSideDistance = Math.abs(
        proposedLeft - attachToRightSide,
      );

      if (
        attachToRightSideDistance <= snapDistance &&
        attachToRightSideDistance < bestX.distance
      ) {
        bestX = {
          distance: attachToRightSideDistance,
          value: attachToRightSide,
          guide,
        };
      }

      const attachToLeftSide = otherRect.left - movingRect.width - snapGap;
      const attachToLeftSideDistance = Math.abs(
        proposedLeft - attachToLeftSide,
      );

      if (
        attachToLeftSideDistance <= snapDistance &&
        attachToLeftSideDistance < bestX.distance
      ) {
        bestX = {
          distance: attachToLeftSideDistance,
          value: attachToLeftSide,
          guide,
        };
      }
    }

    if (horizontalOverlap) {
      const attachBelow = otherRect.bottom + snapGap;
      const attachBelowDistance = Math.abs(proposedTop - attachBelow);

      if (
        attachBelowDistance <= snapDistance &&
        attachBelowDistance < bestY.distance
      ) {
        bestY = {
          distance: attachBelowDistance,
          value: attachBelow,
          guide,
        };
      }

      const attachAbove = otherRect.top - movingRect.height - snapGap;
      const attachAboveDistance = Math.abs(proposedTop - attachAbove);

      if (
        attachAboveDistance <= snapDistance &&
        attachAboveDistance < bestY.distance
      ) {
        bestY = {
          distance: attachAboveDistance,
          value: attachAbove,
          guide,
        };
      }
    }

    const centerXValue = otherRect.centerX - movingRect.width / 2;
    const centerXDistance = Math.abs(movingRect.centerX - otherRect.centerX);

    if (
      centerXDistance <= centerSnapDistance &&
      centerXDistance < bestX.distance
    ) {
      bestX = {
        distance: centerXDistance,
        value: centerXValue,
        guide,
      };
    }

    const centerYValue = otherRect.centerY - movingRect.height / 2;
    const centerYDistance = Math.abs(movingRect.centerY - otherRect.centerY);

    if (
      centerYDistance <= centerSnapDistance &&
      centerYDistance < bestY.distance
    ) {
      bestY = {
        distance: centerYDistance,
        value: centerYValue,
        guide,
      };
    }
  });

  let guide = null;

  if (bestX.distance !== Infinity) {
    snappedLeft = bestX.value;
    guide = bestX.guide;
  }

  if (bestY.distance !== Infinity) {
    snappedTop = bestY.value;
    guide = bestY.guide || guide;
  }

  return {
    left: Math.round(snappedLeft),
    top: Math.round(snappedTop),
    snappedX: Math.round(snappedLeft),
    snappedY: Math.round(snappedTop),
    guide,
    didSnap: bestX.distance !== Infinity || bestY.distance !== Infinity,
  };
}

export function snapTablePosition(
  tables,
  movingTableId,
  proposedLeft,
  proposedTop,
  width = 50,
  height = 50,
  options = {},
) {
  return getBestSnapPosition({
    tableId: movingTableId,
    proposedLeft,
    proposedTop,
    width,
    height,
    otherTables: tables,
    options,
  });
}
